import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Zap, Lock, Users, TrendingUp, Clock, Sparkles, Menu, X, ChevronDown, FileText, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Footer from '../components/Footer';
import LanguageSwitcher from '../components/LanguageSwitcher';

const HowItWorks = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-lg border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/logo.svg"
                alt="Social Octo Logo"
                className="w-10 h-10"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Social Octo
              </span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-gray-700 hover:text-blue-600 font-medium transition">
                {t('nav.features')}
              </Link>
              <Link to="/#pricing" className="text-gray-700 hover:text-blue-600 font-medium transition">
                {t('nav.pricing')}
              </Link>

              {/* Dokümanlar Dropdown */}
              <div className="relative group">
                <button className="flex items-center gap-1 text-gray-700 hover:text-blue-600 font-medium transition py-2">
                  <FileText className="w-4 h-4" />
                  {t('howItWorks.nav.docs')}
                  <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                </button>

                {/* Dropdown Menu - görünmez köprü ile */}
                <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                    <Link
                      to="/how-it-works"
                      className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                    >
                      <Zap className="w-5 h-5" />
                      <div>
                        <div className="font-medium">{t('howItWorks.nav.howItWorks.title')}</div>
                        <div className="text-xs text-gray-500">{t('howItWorks.nav.howItWorks.subtitle')}</div>
                      </div>
                    </Link>
                    <Link
                      to="/platform-requirements"
                      className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                    >
                      <Shield className="w-5 h-5" />
                      <div>
                        <div className="font-medium">{t('howItWorks.nav.platformRequirements.title')}</div>
                        <div className="text-xs text-gray-500">{t('howItWorks.nav.platformRequirements.subtitle')}</div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>

              <LanguageSwitcher />

              <Link to="/login" className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 text-gray-700 hover:from-blue-600 hover:to-purple-600 hover:border-transparent hover:text-white font-medium transition px-5 py-1.5 rounded-lg">
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition"
              >
                {t('nav.register')}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-4">
                <Link to="/#features" className="text-gray-700 hover:text-blue-600 font-medium transition">
                  {t('nav.features')}
                </Link>
                <Link to="/#pricing" className="text-gray-700 hover:text-blue-600 font-medium transition">
                  {t('nav.pricing')}
                </Link>

                {/* Dokümanlar Section */}
                <div className="border-t border-gray-200 pt-4 mt-2">
                  <div className="flex items-center gap-2 text-gray-500 font-semibold text-sm mb-3">
                    <FileText className="w-4 h-4" />
                    {t('howItWorks.nav.docs')}
                  </div>
                  <div className="pl-6 space-y-3">
                    <Link to="/how-it-works" className="flex items-start gap-2 text-gray-700 hover:text-blue-600 font-medium transition">
                      <Zap className="w-4 h-4 mt-0.5" />
                      <div>
                        <div>{t('howItWorks.nav.howItWorks.title')}</div>
                        <div className="text-xs text-gray-500">{t('howItWorks.nav.mobileSubtitle')}</div>
                      </div>
                    </Link>
                    <Link to="/platform-requirements" className="flex items-start gap-2 text-gray-700 hover:text-blue-600 font-medium transition">
                      <Shield className="w-4 h-4 mt-0.5" />
                      <div>
                        <div>{t('howItWorks.nav.platformRequirements.title')}</div>
                        <div className="text-xs text-gray-500">{t('howItWorks.nav.mobileRequirements')}</div>
                      </div>
                    </Link>
                  </div>
                </div>

                <LanguageSwitcher variant="compact" />

                <Link to="/login" className="text-gray-700 hover:text-blue-600 font-medium transition pt-2 border-t border-gray-200">
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold text-center"
                >
                  {t('nav.register')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            {t('howItWorks.hero.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('howItWorks.hero.subtitle')}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">🔒 {t('howItWorks.features.secureConnection.title')}</h3>
            <p className="text-gray-600">
              {t('howItWorks.features.secureConnection.description')}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">⏰ {t('howItWorks.features.scheduledSharing.title')}</h3>
            <p className="text-gray-600">
              {t('howItWorks.features.scheduledSharing.description')}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-pink-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">📊 {t('howItWorks.features.multiPlatformSharing.title')}</h3>
            <p className="text-gray-600">
              {t('howItWorks.features.multiPlatformSharing.description')}
            </p>
          </div>
        </div>

        {/* Step by Step Guide */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 mb-16">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            📚 {t('howItWorks.guide.title')}
          </h2>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Users className="w-6 h-6 text-blue-600" />
                  {t('howItWorks.guide.step1.title')}
                </h3>
                <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-100">
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step1.emailRegistration.label')}</strong> {t('howItWorks.guide.step1.emailRegistration.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step1.googleLogin.label')}</strong> {t('howItWorks.guide.step1.googleLogin.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step1.freeTrial.label')}</strong> {t('howItWorks.guide.step1.freeTrial.description')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Lock className="w-6 h-6 text-blue-600" />
                  {t('howItWorks.guide.step2.title')}
                </h3>
                <div className="bg-purple-50 rounded-xl p-6 border-2 border-purple-100">
                  <p className="text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: t('howItWorks.guide.step2.intro') }}></p>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step2.platformAdd.label')}</strong> {t('howItWorks.guide.step2.platformAdd.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step2.secureConnection.label')}</strong> {t('howItWorks.guide.step2.secureConnection.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step2.confirmation.label')}</strong> {t('howItWorks.guide.step2.confirmation.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Lock className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-sm text-gray-600">
                        <strong>{t('howItWorks.guide.step2.securityNote.label')}</strong> {t('howItWorks.guide.step2.securityNote.description')}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Zap className="w-6 h-6 text-blue-600" />
                  {t('howItWorks.guide.step3.title')}
                </h3>
                <div className="bg-pink-50 rounded-xl p-6 border-2 border-pink-100">
                  <p className="text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: t('howItWorks.guide.step3.intro') }}></p>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step3.enterText.label')}</strong> {t('howItWorks.guide.step3.enterText.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step3.addMedia.label')}</strong> {t('howItWorks.guide.step3.addMedia.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step3.selectPlatform.label')}</strong> {t('howItWorks.guide.step3.selectPlatform.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step3.scheduling.label')}</strong> {t('howItWorks.guide.step3.scheduling.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step3.publish.label')}</strong> {t('howItWorks.guide.step3.publish.description')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                  {t('howItWorks.guide.step4.title')}
                </h3>
                <div className="bg-green-50 rounded-xl p-6 border-2 border-green-100">
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step4.postList.label')}</strong> {t('howItWorks.guide.step4.postList.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step4.statusTracking.label')}</strong> {t('howItWorks.guide.step4.statusTracking.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step4.editing.label')}</strong> {t('howItWorks.guide.step4.editing.description')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>{t('howItWorks.guide.step4.statistics.label')}</strong> {t('howItWorks.guide.step4.statistics.description')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 mb-16">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            ❓ {t('howItWorks.faq.title')}
          </h2>

          <div className="space-y-6">
            <div className="border-b-2 border-gray-100 pb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('howItWorks.faq.isFree.question')}</h3>
              <p className="text-gray-600">
                {t('howItWorks.faq.isFree.answer')}
              </p>
            </div>

            <div className="border-b-2 border-gray-100 pb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('howItWorks.faq.supportedPlatforms.question')}</h3>
              <p className="text-gray-600">
                {t('howItWorks.faq.supportedPlatforms.answer')}
              </p>
            </div>

            <div className="border-b-2 border-gray-100 pb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('howItWorks.faq.dataSecurity.question')}</h3>
              <p className="text-gray-600">
                {t('howItWorks.faq.dataSecurity.answer')}
              </p>
            </div>

            <div className="border-b-2 border-gray-100 pb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('howItWorks.faq.postLimit.question')}</h3>
              <p className="text-gray-600">
                {t('howItWorks.faq.postLimit.answer')}
              </p>
            </div>

            <div className="pb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('howItWorks.faq.support.question')}</h3>
              <p className="text-gray-600" dangerouslySetInnerHTML={{ __html: t('howItWorks.faq.support.answer') }}></p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl shadow-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">{t('howItWorks.cta.title')} 🚀</h2>
          <p className="text-xl mb-8 opacity-90">
            {t('howItWorks.cta.subtitle')}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link
              to="/register"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition transform hover:scale-105"
            >
              {t('howItWorks.cta.freeRegister')}
            </Link>
            <Link
              to="/login"
              className="bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-800 transition border-2 border-white"
            >
              {t('howItWorks.cta.login')}
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HowItWorks;
